import { HomeFeed } from 'Api/Feed/feed.types'
import type {
  EventGQL,
  Banner,
  TeamStanding,
  Feed,
  ShopProduct,
  PlayerTransactions,
  FeedItem,
} from '../../Api'
import type { LeaderResult } from './types'

// Action type constants
export const LEAGUE_ACTIONS = {
  SET_EVENTS: 'SET_EVENTS',
  SET_EVENTS_LOADING: 'SET_EVENTS_LOADING',
  SET_CURRENT_WEEK: 'SET_CURRENT_WEEK',
  SET_STANDINGS: 'SET_STANDINGS',
  SET_FEED: 'SET_FEED',
  SET_HOME_FEED: 'SET_HOME_FEED',
  SET_TEAM_FEED: 'SET_TEAM_FEED',
  SET_IS_FETCHING_MAIN_FEED: 'SET_IS_FETCHING_MAIN_FEED',
  SET_IS_FETCHING_TEAM_FEED: 'SET_IS_FETCHING_TEAM_FEED',
  SET_FEED_LOADING: 'SET_FEED_LOADING',
  SET_LEADERS: 'SET_LEADERS',
  SET_PLAYER_TRANSACTIONS: 'SET_PLAYER_TRANSACTIONS',
  SET_BANNERS: 'SET_BANNERS',
  SET_BEST_SELLING_PRODUCTS: 'SET_BEST_SELLING_PRODUCTS',
  SET_HAS_CHAMP_SERIES_STARTED: 'SET_HAS_CHAMP_SERIES_STARTED',
  UPDATE_EVENT_IN_STATE: 'UPDATE_EVENT_IN_STATE',
} as const

export type LeagueState = {
  events: EventGQL[]
  eventsLoading: boolean
  currentWeek: null | string
  standings: TeamStanding[]
  feed: Feed | null
  homeFeed: HomeFeed | null
  teamFeed: FeedItem[]
  isFetchingMainFeed: boolean
  isFetchingTeamFeed: boolean
  feedLoading: boolean
  leaders: LeaderResult[]
  playerTransactions: PlayerTransactions[]
  banners: Banner[]
  bestSellingProducts: ShopProduct[]
  hasChampSeriesStarted: boolean
}

export type LeagueAction =
  | { type: typeof LEAGUE_ACTIONS.SET_EVENTS; payload: EventGQL[] }
  | { type: typeof LEAGUE_ACTIONS.SET_EVENTS_LOADING; payload: boolean }
  | { type: typeof LEAGUE_ACTIONS.SET_CURRENT_WEEK; payload: string | null }
  | { type: typeof LEAGUE_ACTIONS.SET_STANDINGS; payload: TeamStanding[] }
  | { type: typeof LEAGUE_ACTIONS.SET_FEED; payload: Feed | null }
  | { type: typeof LEAGUE_ACTIONS.SET_HOME_FEED; payload: HomeFeed | null }
  | { type: typeof LEAGUE_ACTIONS.SET_TEAM_FEED; payload: FeedItem[] }
  | { type: typeof LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED; payload: boolean }
  | { type: typeof LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED; payload: boolean }
  | { type: typeof LEAGUE_ACTIONS.SET_FEED_LOADING; payload: boolean }
  | { type: typeof LEAGUE_ACTIONS.SET_LEADERS; payload: LeaderResult[] }
  | {
      type: typeof LEAGUE_ACTIONS.SET_PLAYER_TRANSACTIONS
      payload: PlayerTransactions[]
    }
  | { type: typeof LEAGUE_ACTIONS.SET_BANNERS; payload: Banner[] }
  | { type: typeof LEAGUE_ACTIONS.SET_BEST_SELLING_PRODUCTS; payload: ShopProduct[] }
  | { type: typeof LEAGUE_ACTIONS.SET_HAS_CHAMP_SERIES_STARTED; payload: boolean }
  | { type: typeof LEAGUE_ACTIONS.UPDATE_EVENT_IN_STATE; payload: EventGQL }

export const initialState: LeagueState = {
  events: [],
  eventsLoading: true,
  currentWeek: null,
  standings: [],
  feed: null,
  homeFeed: null,
  teamFeed: [],
  isFetchingMainFeed: false,
  isFetchingTeamFeed: false,
  feedLoading: true,
  leaders: [],
  playerTransactions: [],
  banners: [],
  bestSellingProducts: [],
  hasChampSeriesStarted: false,
}

export const leagueReducer = (state: LeagueState, action: LeagueAction): LeagueState => {
  switch (action.type) {
    case LEAGUE_ACTIONS.SET_EVENTS:
      return { ...state, events: action.payload }
    case LEAGUE_ACTIONS.SET_EVENTS_LOADING:
      return { ...state, eventsLoading: action.payload }
    case LEAGUE_ACTIONS.SET_CURRENT_WEEK:
      return { ...state, currentWeek: action.payload }
    case LEAGUE_ACTIONS.SET_STANDINGS:
      return { ...state, standings: action.payload }
    case LEAGUE_ACTIONS.SET_FEED:
      return { ...state, feed: action.payload }
    case LEAGUE_ACTIONS.SET_HOME_FEED:
      return { ...state, homeFeed: action.payload }
    case LEAGUE_ACTIONS.SET_TEAM_FEED:
      return { ...state, teamFeed: action.payload }
    case LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED:
      return { ...state, isFetchingMainFeed: action.payload }
    case LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED:
      return { ...state, isFetchingTeamFeed: action.payload }
    case LEAGUE_ACTIONS.SET_FEED_LOADING:
      return { ...state, feedLoading: action.payload }
    case LEAGUE_ACTIONS.SET_LEADERS:
      return { ...state, leaders: action.payload }
    case LEAGUE_ACTIONS.SET_PLAYER_TRANSACTIONS:
      return { ...state, playerTransactions: action.payload }
    case LEAGUE_ACTIONS.SET_BANNERS:
      return { ...state, banners: action.payload }
    case LEAGUE_ACTIONS.SET_BEST_SELLING_PRODUCTS:
      return { ...state, bestSellingProducts: action.payload }
    case LEAGUE_ACTIONS.SET_HAS_CHAMP_SERIES_STARTED:
      return { ...state, hasChampSeriesStarted: action.payload }
    case LEAGUE_ACTIONS.UPDATE_EVENT_IN_STATE:
      return {
        ...state,
        events: state.events.map((e) =>
          e.id === action.payload.id ? action.payload : e
        ),
      }
    default:
      return state
  }
}
